import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { LogOut, Layout, Languages, Menu, BookOpen, Users, IceCream, Mic, HeartHandshake } from 'lucide-react';
import ThemeToggle from '../ThemeToggle';

/**
 * Dashboard Header Component
 * Displays logo, user info, language toggle, theme toggle, and hamburger menu
 */
const DashboardHeader = ({ 
  user, 
  onMenuToggle, 
  onLayoutCustomize, 
  onLogout 
}) => {
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();

  return (
    <header className="gradient-primary border-b border-white/20 px-3 sm:px-6 py-3 sm:py-4 sticky top-0 z-50 shadow-lg">
      <div className="flex items-center justify-between">
        {/* Left: Logo + Menu */}
        <div className="flex items-center gap-2 sm:gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            className="px-2 text-white hover:bg-white/20 border border-white/30"
            onClick={onMenuToggle}
          >
            <Menu className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-2">
            <span className="text-2xl">✝️</span>
            <h1 className="text-lg sm:text-xl font-bold text-white hidden xs:block">
              Lumera
            </h1>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Daily Devotional Sundae shortcut */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/community?tab=games&sundae=start')}
            className="hidden md:flex text-white hover:bg-white/20 border border-yellow-200/60 px-2 sm:px-3 bg-white/5"
            title={t('header.sundaeTitle')}
            data-testid="header-sundae-btn"
          >
            <IceCream className="w-4 h-4 sm:mr-1" />
            <span className="hidden md:inline text-xs font-semibold">
              {t('header.sundae')}
            </span>
          </Button>

          {/* Community Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/community')}
            className="hidden md:flex text-white hover:bg-white/20 border border-white/30 px-2 sm:px-3"
            title={t('header.community')}
          >
            <Users className="w-4 h-4 sm:mr-1" />
            <span className="hidden md:inline text-xs font-semibold">
              {t('header.community')}
            </span>
          </Button>

          {/* Prayer Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/prayer')}
            className="hidden md:flex text-white hover:bg-white/20 border border-white/30 px-2 sm:px-3"
            title={t('header.prayer')}
            data-testid="header-prayer-btn"
          >
            <HeartHandshake className="w-4 h-4 sm:mr-1" />
            <span className="hidden md:inline text-xs font-semibold">
              {t('header.prayer')}
            </span>
          </Button>

          {/* Open Bible Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/bible')}
            className="hidden md:flex text-white hover:bg-white/20 border border-white/30 px-2 sm:px-3"
            title={t('header.openBible')}
          >
            <BookOpen className="w-4 h-4 sm:mr-1" />
            <span className="hidden md:inline text-xs font-semibold">
              {t('header.bible')}
            </span>
          </Button>

          {/* Sermon Prep Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/sermon-prep')}
            className="hidden md:flex text-white hover:bg-white/20 border border-white/30 px-2 sm:px-3"
            title={t('header.sermonPrep')}
            data-testid="header-sermon-btn"
          >
            <Mic className="w-4 h-4 sm:mr-1" />
            <span className="hidden md:inline text-xs font-semibold">
              {t('header.sermon')}
            </span>
          </Button>

          {/* Theme Toggle */}
          <div className="hidden sm:block">
            <ThemeToggle />
          </div>

          {/* Language Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            className="text-white hover:bg-white/20 border border-white/30 px-2 sm:px-3"
            title={t('header.switchLang')}
            data-testid="language-toggle"
          >
            <Languages className="w-4 h-4 mr-1" />
            <span className="text-xs font-semibold">
              {language === 'es' ? 'ES' : 'EN'}
            </span>
          </Button>

          {/* Layout Customizer (Desktop only) */}
          <Button
            variant="ghost"
            size="sm"
            className="hidden lg:flex text-white hover:bg-white/20 border border-white/30 px-2 sm:px-3"
            onClick={onLayoutCustomize}
          >
            <Layout className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline text-xs">
              {t('header.layout')}
            </span>
          </Button>

          {/* Logout Button */}
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-btn"
            onClick={onLogout}
            className="text-white hover:bg-white/20 border border-white/30 px-2 sm:px-3"
            title={t('header.logout')}
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
